import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Package, Truck, ClipboardText, Warning, ArrowsClockwise, ChartBar, ListChecks, Wrench } from "@phosphor-icons/react";
import { MaterialsTab } from "@/components/admin/inventory/MaterialsTab";
import { VendorsTab } from "@/components/admin/inventory/VendorsTab";
import { PurchaseOrdersTab } from "@/components/admin/inventory/PurchaseOrdersTab";
import { MovementsTab } from "@/components/admin/inventory/MovementsTab";
import { AlertsStrip } from "@/components/admin/inventory/AlertsStrip";
import { BatchesTab } from "@/components/admin/inventory/BatchesTab";
import { CustomFieldsSettingsTab } from "@/components/admin/inventory/CustomFieldsSettingsTab";
import WastageTrendChart from "@/components/admin/inventory/WastageTrendChart";
import AdminSegmentedTabs from "@/components/admin/AdminSegmentedTabs";


/* ============================================================================
   AdminInventory — `/admin/inventory`
   Command Center for production materials. 4 tabs + a live low-stock alerts
   strip pinned under the hero.
   ========================================================================== */

const TABS = [
    { id: "materials",        label: "Materials",        Icon: Package },
    { id: "batches",          label: "Batches / Lots",   Icon: ListChecks },
    { id: "vendors",          label: "Vendors",          Icon: Truck },
    { id: "purchase-orders",  label: "Purchase Orders",  Icon: ClipboardText },
    { id: "movements",        label: "Stock Movements",  Icon: ArrowsClockwise },
    { id: "settings",         label: "Field Settings",   Icon: Wrench },
];

export default function AdminInventory() {
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = TABS.some((t) => t.id === searchParams.get("tab")) ? searchParams.get("tab") : "materials";
    const [tab, setTab] = useState(initialTab);
    const [overview, setOverview] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        const urlTab = searchParams.get("tab");
        if (tab !== urlTab) {
            if (tab === "materials") {
                searchParams.delete("tab");
            } else {
                searchParams.set("tab", tab);
            }
            setSearchParams(searchParams, { replace: true });
        }
    }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        api.get("/admin/inventory/overview").then((r) => setOverview(r.data)).catch(() => {});
    }, [reloadKey]);

    const reload = () => setReloadKey((k) => k + 1);

    return (
        <div className="space-y-6" data-testid="admin-inventory-page">
            <div>
                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Operations · Inventory</div>
                <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5">Inventory Command Center</h1>
                <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                    Every resin, sheet, and bottle — vendor to production, with POs and low-stock alerts.
                </p>
            </div>

            {overview && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="inventory-kpi-strip">
                    <Kpi label="Materials"       value={overview.materials_count}          Icon={Package}         tone="primary" />
                    <Kpi label="Active vendors"  value={overview.vendors_active}           Icon={Truck}           tone="violet" />
                    <Kpi label="Open POs"        value={overview.po_open}                  Icon={ClipboardText}   tone="amber" />
                    <Kpi label="Pending payment" value={overview.po_pending_pay}           Icon={ChartBar}        tone="emerald" />
                    <Kpi label="Open PO value"   value={formatINR(overview.open_po_value)} Icon={ChartBar}        tone="primary" />
                    <Kpi label="Low stock"       value={overview.low_stock_count}          Icon={Warning}         tone={overview.low_stock_count > 0 ? "rose" : "emerald"} />
                </div>
            )}

            <AlertsStrip onReload={reloadKey} />

            {/* iter97.21 — 14-day rolling wastage trend. Surfaces material-defect spikes
                early so admin can flag SOP drift before it eats margin. */}
            <WastageTrendChart />

            <AdminSegmentedTabs
                tabs={TABS}
                active={tab}
                onChange={setTab}
                testid="inventory-tabs"
                tabTestidBase="inventory-tab"
            />

            <div>
                {tab === "materials"        && <MaterialsTab onChange={reload} />}
                {tab === "batches"          && <BatchesTab />}
                {tab === "vendors"          && <VendorsTab onChange={reload} />}
                {tab === "purchase-orders"  && <PurchaseOrdersTab onChange={reload} />}
                {tab === "movements"        && <MovementsTab />}
                {tab === "settings"         && <CustomFieldsSettingsTab />}
            </div>
        </div>
    );
}


const formatINR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

/* Canonical StatTile — matches every Work/Content section tile. */
function Kpi({ label, value, Icon, tone = "primary" }) {
    const accent = tone === "emerald" ? "bg-emerald-100 text-emerald-700"
        : tone === "amber" ? "bg-amber-100 text-amber-700"
        : tone === "rose" ? "bg-rose-100 text-rose-700"
        : tone === "slate" ? "bg-slate-100 text-slate-700"
        : tone === "violet" ? "bg-violet-100 text-violet-700"
        : "bg-primary/10 text-primary";
    return (
        <div className="flex items-start gap-3 rounded-xl bg-card border border-border p-3">
            <div className={`w-9 h-9 rounded-full ${accent} grid place-items-center shrink-0`}>
                <Icon size={15} weight="fill" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
                <div className="text-xl font-display font-bold tabular-nums leading-none mt-1 truncate">{value}</div>
            </div>
        </div>
    );
}
